/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */
import { connect } from 'react-redux';
import './CostBasisMethod.scss';
import './Registration.scss';
import './EditModal.scss';
import { getCostBasisRegistrationTable } from 'state/taxes/TaxSelectors.js';
import infoHelpIcon from 'assets/icons/info-help-icon-filled.svg';
import ToolTip from 'components/shared/ToolTip/ToolTip';
import redInfoIcon from 'assets/icons/red-info-icon.svg';
import { ReactComponent as QuestionCircle } from 'assets/icons/question-circle.svg';

import React, { Fragment, useState } from 'react';
import EditModalDisclosure from './EditModalDisclosure';
import {
  hasMutualFunds,
  isSalesforceRepEnabled
} from 'state/view/ViewSelectors';
import { getCustomerWebView } from 'state/customer/CustomerSelectors';
import { isMobileLayout } from 'state/view/ViewSelectors';
import { AAMBeaconTooltip } from 'components/shared/Beacon';
import { isActiveAdvisory } from 'services/Products';
import DisclosureModal from 'components/shared/DisclosureModal/DisclosureModal';
import { ReactComponent as LockIcon } from 'assets/icons/lock-3.svg';
import { ReactComponent as ToolTipIcon } from 'assets/icons/tooltip.svg';
import Content from 'services/Content';
import EditModal from './EditModal';
import { hasOwnedActiveAdvisory } from 'state/account/AccountSelectors';
import ShareClassConversionModal from "components/shared/ShareClassConversion/ShareClassConversionModal";
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { appBaseUrl } from 'services/Externals';

const Registration = ({
                        costBasisRegistrationTable,
                        hasMutualFund,
                        hasActiveAdvisory,
                        isCustomerWebView,
                        submitCostBasis,
                        isSalesforceRepEnabled,
                        requestStatus
                      }) => {
  const minimizedShortTermModal = (
    <DisclosureModal
      linkContent={'Minimized Short Term Gains'}
      linkContentClass="min-short-term-gains-link"
      icon={<LockIcon slot="suffix" />}
      closeOnClickOut={true}
      size={'Normal'}>
      <Fragment>
        <div className="b-typography-serif--headline--200">Retirement Advisory Service</div>
        <div className="b-typography-sans-serif--body--200 b-font-weight-light">
          {Content.minimizedShortTermGainsDisclosure}
        </div>
      </Fragment>
    </DisclosureModal>
  );

  const pendingChangesModal = (
    <DisclosureModal
      costBasis={true}
      linkContent={'Changes Pending'}
      linkContentClass="pending-changes-link"
      icon={
        <ToolTipIcon
          slot="suffix"
          className="pending-changes-link--icon"
          viewBox="0 0 17 17"
        />
      }
      closeOnClickOut={true}>
      <Fragment>
        <div className="b-typography-serif--headline--200">Changes Pending</div>
        <div className="b-typography-sans-serif--body--200 b-font-weight-light">
          {Content.costBasisPendingChanges}
        </div>
      </Fragment>
    </DisclosureModal>
  );

  const pendingTransactionsModal = (
    <DisclosureModal
      linkContent={'This account has pending transactions.'}
      linkContentClass="pending-transactions-link"
      link={`${appBaseUrl()}/usis/my-accounts/transactions/`}
      icon={<LockIcon slot="suffix" />}
      size={'Normal'}
    />
  );

  const [openModal, setOpenModal] = useState(false);
  const characterLimit = isMobileLayout ? 30 : 12;
  const [selectedCostBasis, setSelectedCostBasis] = useState({});

  const accountTypes = Object.keys(costBasisRegistrationTable);
  const accountsToSelect = accountTypes.map((accountType, i) => {
    const accountList = costBasisRegistrationTable[accountType];
    const registrationName =
      accountType.length > characterLimit
        ? <>
            <h2 id={`tooltip-registration-trigger-${i}`} className="registration-header-name b-font-weight-bold b-typography-sans-serif--headline--100 blocked_data">
              {accountType.substring(0, characterLimit)} ...)
            </h2>
            <AAMBeaconTooltip
              className={'beacon-on-dark'}
              opens-by={`#tooltip-registration-trigger-${i}`}
              placement={'right'}
            >
              <div>{accountType}</div>
            </AAMBeaconTooltip>
        </>
        : <h2 className="registration-header-name b-font-weight-bold b-typography-sans-serif--headline--100 blocked_data">{accountType}</h2>;
    return (
      <div className="registration-container" key={`registration-${i}`}>
        <div className="registration-box">
          <div className="registration-header">
            {registrationName}
          </div>
          <div className="account-type-funds accounts-container">
            <div className="account-type-headers">
              <div className={'account-row-headers'}>
                <div
                  className={
                    hasActiveAdvisory
                      ? 'minimized-gains-account'
                      : 'account-info-1'
                  }>
                  ACCOUNT
                </div>
                <div
                  className={
                    hasActiveAdvisory
                      ? 'minimized-gains-election cost-basis-v2-table-header'
                      : 'account-info-2 cost-basis-v2-table-header'
                  }>
                  COST BASIS METHOD *
                </div>
                <div
                  className={
                    hasActiveAdvisory
                      ? 'minimized-gains-modal'
                      : 'account-info-3'
                  }
                />
              </div>
            </div>
            {accountList.map((account, j) => {
              const {
                accountNameMod,
                numberWithChecksum,
                costBasisMethodMod,
                productType,
                pendingIndicator,
                ticker,
                hasPendingTransaction,
                isBrokerageStockReinvestment,
                isBrokerage,
                shareClassConversion,
                closedAccount
              } = account;
              const costBasisMethodName =
                isBrokerage &&
                account?.brokerageCostBasisMethods?.recordKeeperStatus
                  .brokerageRequestStatus === 'UNKNOWN' ? (
                  <div className="cost-basis-method-select cost-basis-method-select-error-msg">
                    <img src={redInfoIcon} alt="info-icon" />
                    <span className={'b-margin-inline-start--micro--mini b-font-weight-bold b-typography-sans-serif--caption--200'}>
                        Currently Unavailable
                    </span>
                  </div>
                ) : (
                  <Fragment>{costBasisMethodMod}</Fragment>
                );
              const editModalDisclosure = (
                <EditModalDisclosure
                  account={account}
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  index={j}
                  accountType={accountType}
                  hasMutualFund={hasMutualFund}
                  submitCostBasis={submitCostBasis}
                  setSelectedCostBasis={setSelectedCostBasis}
                />
              );
              const costBasisModal = isActiveAdvisory(productType)
                ? minimizedShortTermModal
                : hasPendingTransaction
                  ? pendingTransactionsModal
                  : pendingIndicator ||
                  (isBrokerage &&
                    account?.brokerageCostBasisMethods?.recordKeeperStatus
                      .brokerageRequestStatus === 'PENDING')
                    ? pendingChangesModal
                    : isBrokerage &&
                    account?.brokerageCostBasisMethods?.recordKeeperStatus
                      .brokerageRequestStatus === 'UNKNOWN'
                      ? null
                      : editModalDisclosure;
              const showCostBasisMethod =
                !isActiveAdvisory(productType) && !pendingIndicator;
              return (
                // TODO: split into separate shared component
                <div key={j} className={'account-content'}>
                  <div className={`account-row-info account-${j}`}>
                    <div
                      className={
                        hasActiveAdvisory
                          ? 'minimized-gains-account'
                          : 'account-info-1'
                      }>
                      <p className="account-ticker b-font-weight-light b-typography-sans-serif--caption--200 blocked_data">
                          {ticker}
                      </p>
                      <div className="account-accountName b-margin-block-end--micro--mini b-font-weight-light b-typography-sans-serif--body--100">
                        {accountNameMod}{' '}
                        {isBrokerageStockReinvestment && (
                          <ToolTip
                            className="cost-basis-tooltip-v2"
                            tooltipLabel={
                              <div>
                                <div className="cost-basis-tooltip-v2-header b-font-weight-bold b-typography-sans-serif--body--100">
                                  Stocks in Dividend Reinvestment Plans
                                </div>
                                <div className="cost-basis-tooltip-v2-content b-typography-sans-serif--body--100">
                                  {Content.costBasisStockDividendPlanV2}
                                </div>
                              </div>
                            }>
                            <img src={infoHelpIcon} alt="info" />
                          </ToolTip>
                        )}
                      </div>
                      <p className="account-check-sum b-font-weight-light b-typography-sans-serif--caption--300 blocked_data">
                        {numberWithChecksum}
                      </p>
                    </div>
                    <div
                      className={
                        hasActiveAdvisory
                          ? 'account-cost-basis-election minimized-gains-election'
                          : 'account-cost-basis-election account-info-2 b-font-weight-bold b-typography-sans-serif--caption--300'
                      }>
                      {showCostBasisMethod && costBasisMethodName}
                    </div>
                    <div
                      className={
                        hasActiveAdvisory
                          ? 'minimized-gains-modal'
                          : pendingIndicator
                            ? 'cost-basis-pending-modal-v2'
                            : `account-info-3 ${
                              showCostBasisMethod
                                ? ''
                                : 'cost-basis-shortTermOrPending-modal-v2'
                            }`
                      }>
                      {costBasisModal}
                    </div>
                  </div>
                  <div className="account-label-container">
                    {closedAccount && (
                      <div className="account-label" aria-label="Closed">
                        Closed
                      </div>
                    )}
                    {shareClassConversion?.activeShareClassConverted && (
                      <div className="account-label-with-icon">
                        <div
                          className="account-label"
                          aria-label="Share Class Conversion">
                          Share Class Conversion
                        </div>
                        <ShareClassConversionModal
                          icon={<QuestionCircle slot="suffix" />}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className="registration-container">
      {accountsToSelect}
      <EditModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        account={selectedCostBasis.account}
        accountType={selectedCostBasis.accountType}
        hasMutualFund={selectedCostBasis.hasMutualFund}
        currentCostBasis={selectedCostBasis.currentCostBasis}
        submitCostBasis={submitCostBasis}
        isCustomerWebView={isCustomerWebView}
        isSalesforceRepEnabled={isSalesforceRepEnabled}
        requestStatus={requestStatus}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    costBasisRegistrationTable: getCostBasisRegistrationTable(state),
    hasActiveAdvisory: hasOwnedActiveAdvisory(state),
    hasMutualFund: hasMutualFunds(state),
    isCustomerWebView: getCustomerWebView(state),
    isSalesforceRepEnabled: isSalesforceRepEnabled(state),
    requestStatus: getRequestStatus(state)
  };
};

export default connect(mapStateToProps)(Registration);
